import SearchIcon from "@mui/icons-material/Search";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MIN_PAGE_SIZE, PAGE_SIZES } from "../../../constants/main";
import { getProjectGroupStatisticActions, useProjectGroupStatisticActionsResponse } from "../../../hooks/projectGroup";
import { usePagination } from "../../../hooks/utils/usePagination";
import { useTimeout } from "../../../hooks/utils/useTimeout";
import { processMessage, roundDecimal } from "../../../utils";
import { ContentBlock } from "../../ContentBlock/ContentBlock";
import { LoadingOverlay } from "../../LoadingOverlay/LoadingOverlay";
import { SimpleTableWithSort } from "../../SimpleTableWithSort/SimpleTableWithSort";
import "./ProjectGroupAnalyticsActions.scss";

export const ProjectGroupAnalyticsActions = React.memo(({ projectGroupId }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [processed, setProcessed] = useState([]);
  const [paged, setPaged] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [sortValue, setSortValue] = useState({ field: "actionName" });
  const [, setSearchTimeout] = useTimeout();
  const { data, loading } = useProjectGroupStatisticActionsResponse();
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePagination(0, MIN_PAGE_SIZE);
  const total = processed.length;

  useEffect(() => {
    getProjectGroupStatisticActions(projectGroupId).then().catch(console.error);
  }, [projectGroupId]);

  useEffect(() => {
    let updated = [...(data || [])];
    if (searchValue) {
      const lowCaseSearchValue = searchValue.toLowerCase();
      updated = updated.filter(
        ({ actionName, minValue, maxValue, avgValue, quantity }) =>
          String(actionName).toLowerCase().indexOf(lowCaseSearchValue) !== -1 ||
          +lowCaseSearchValue === minValue ||
          +lowCaseSearchValue === maxValue ||
          +lowCaseSearchValue === avgValue ||
          +lowCaseSearchValue === quantity
      );
    }
    if (sortValue && sortValue.field) {
      updated.sort((a, b) => {
        if (a[sortValue.field] > b[sortValue.field]) {
          return sortValue.desc ? -1 : 1;
        }
        if (a[sortValue.field] < b[sortValue.field]) {
          return sortValue.desc ? 1 : -1;
        }
        return 0;
      });
    }
    setProcessed(updated);
    setPaged(rowsPerPage < updated.length ? [...updated].splice(page * rowsPerPage, rowsPerPage) : updated);
  }, [searchValue, sortValue, data, page, rowsPerPage]);

  const search = useCallback(
    (event) =>
      setSearchTimeout(
        setTimeout(() => {
          setSearchValue(String(event.target.value || "").trim());
        }, 500)
      ),
    []
  );

  const columns = useMemo(
    () => [
      {
        field: "actionName",
        headerText: t("projectGroup.analytics.actions.actionName"),
        className: "name-column",
        sortable: true,
      },
      {
        field: "quantity",
        headerText: t("projectGroup.analytics.actions.quantity"),
        className: "number-column",
        sortable: true,
      },
      {
        field: "minValue",
        headerText: t("projectGroup.analytics.actions.minValue"),
        className: "number-column",
        template: ({ minValue }) => roundDecimal(minValue, 100),
        sortable: true,
      },
      {
        field: "maxValue",
        headerText: t("projectGroup.analytics.actions.maxValue"),
        className: "number-column",
        template: ({ maxValue }) => roundDecimal(maxValue, 100),
        sortable: true,
      },
      {
        field: "avgValue",
        headerText: t("projectGroup.analytics.actions.avgValue"),
        className: "number-column",
        template: ({ avgValue }) => roundDecimal(avgValue, 100),
        sortable: true,
      },
    ],
    []
  );

  const onSort = (field) =>
    setSortValue({
      field: field !== sortValue.field || !sortValue.desc ? field : null,
      desc: sortValue.field === field ? !sortValue.desc : false,
    });

  const pagination = {
    labelRowsPerPage: t("pagination.rowsPerPage"),
    labelDisplayedRows: ({ from, to, count }) => processMessage(t("pagination.displayedRows"), [from, to, count]),
    rowsPerPageOptions: PAGE_SIZES,
    count: total,
    page: page,
    show: total > MIN_PAGE_SIZE ? "true" : "false",
    onPageChange: (event, pageOnChange) => setPage(pageOnChange),
    rowsPerPage: rowsPerPage,
    onRowsPerPageChange: (event) => setRowsPerPage(event.target.value),
  };

  return (
    <ContentBlock className="project-group-analytics-actions">
      <LoadingOverlay spinner active={loading} className="auto-height">
        <div
          className="search-box"
          onClick={() => {
            inputRef.current.focus();
          }}
        >
          <SearchIcon />
          <input
            type="text"
            defaultValue={searchValue || ""}
            className="search-input input-shadow"
            ref={inputRef}
            placeholder={t("main.search")}
            onChange={search}
          />
        </div>
        <SimpleTableWithSort
          data={paged}
          columns={columns}
          onSort={onSort}
          sortValue={sortValue}
          pagination={pagination}
        />
      </LoadingOverlay>
    </ContentBlock>
  );
});
